import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  fetchBookingsByMobile,
  clearError
} from '../../../store/slices/bookingSlice';
import { showError } from '../../../utils/toastr';
import {
  Search,
  Calendar,
  MapPin,
  Package,
  Clock,
  User,
  Phone,
  Camera,
  FileText,
  Plus,
  CheckCircle,
  AlertCircle,
  HelpCircle,
  Loader2,
  DollarSign
} from 'lucide-react';

const MyBookings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [userData, setUserData] = useState(null);
  const [mobileInput, setMobileInput] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const headerRef = useRef(null);
  const searchRef = useRef(null);
  const bookingsRef = useRef(null);

  const headerInView = useInView(headerRef, { once: true });
  const searchInView = useInView(searchRef, { once: true });
  const bookingsInView = useInView(bookingsRef, { once: true });

  // Get bookings from Redux store
  const { bookings, loading, error } = useSelector(
    (state) => state.bookings
  );

  // Mock data for demonstration (remove this when backend is ready)
  const mockBookings = [
    {
      id: 'BK1759392517978',
      bookingId: 'BK1759392517978',
      createdAt: new Date().toISOString(),
      status: 'pending',
      name: 'Rahul',
      mobile: '',
      scrapType: 'plastic',
      quantity: 'small',
      pickupDate: '2025-09-29',
      pickupTime: 'morning',
      address: 'Sector 16 pkl',
      city: 'Panchkula',
      pincode: '134113',
      landmark: 'Kheda',
      photoUrl: null,
      notes: 'No additional notes provided.',
      estimatedValue: '₹150-200',
      confirmationCall: 'Within 2 hours',
      nextSteps: [
        'Our team will call you within 2 hours to confirm your booking details',
        'We\'ll schedule the pickup at your preferred time and date',
        'Our collector will visit, weigh your scrap, and make instant payment'
      ]
    },
    {
      id: 'BK1759080681792',
      bookingId: 'BK1759080681792',
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      status: 'confirmed',
      name: 'Priya',
      mobile: '',
      scrapType: 'metal',
      quantity: 'medium',
      pickupDate: '2025-09-30',
      pickupTime: 'afternoon',
      address: 'Sector 5',
      city: 'Chandigarh',
      pincode: '160001',
      landmark: 'Near Market',
      photoUrl: null,
      notes: 'Please call before coming.',
      estimatedValue: '₹800-1200',
      confirmationCall: 'Completed',
      nextSteps: [
        'Pickup scheduled for tomorrow afternoon',
        'Our collector will arrive between 2-4 PM',
        'Payment will be made after weighing your scrap'
      ]
    },
    {
      id: 'BK1759080681793',
      bookingId: 'BK1759080681793',
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      status: 'completed',
      name: 'Amit',
      mobile: '',
      scrapType: 'paper',
      quantity: 'large',
      pickupDate: '2025-09-28',
      pickupTime: 'morning',
      address: 'Sector 22',
      city: 'Chandigarh',
      pincode: '160022',
      landmark: 'Near Hospital',
      photoUrl: null,
      notes: 'Completed successfully.',
      estimatedValue: '₹300-400',
      confirmationCall: 'Completed',
      actualPayment: '₹350',
      nextSteps: [
        'Pickup completed successfully',
        'Payment of ₹350 received',
        'Thank you for choosing ScrapSector!'
      ]
    }
  ];

  useEffect(() => {
    // Load user data from localStorage
    const savedUserData = localStorage.getItem('userData');

    if (savedUserData) {
      const user = JSON.parse(savedUserData);
      setUserData(user);

      if (user.mobile) {
        setMobileInput(user.mobile);

        // Try to fetch from backend
        dispatch(fetchBookingsByMobile(user.mobile))
          .unwrap()
          .then((data) => {
            console.log('Auto-loaded bookings:', data);
            setHasSearched(true);
          })
          .catch((error) => {
            console.log('Backend error:', error);
            setHasSearched(true);
          });
      }
    } else {
      setMobileInput('');
    }
  }, [dispatch]);

  // Handle error display
  useEffect(() => {
    if (error) {
      showError(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return {
          text: 'Pending Confirmation',
          color: 'from-[#2F6F45] to-[#70BD7D]',
          bgColor: 'bg-[#EAF5EC]',
          textColor: 'text-[#2F6F45]',
          icon: AlertCircle
        };

      case 'confirmed':
        return {
          text: 'Confirmed',
          color: 'from-[#102018] to-[#2F6F45]',
          bgColor: 'bg-[#EAF5EC]',
          textColor: 'text-[#2F6F45]',
          icon: CheckCircle
        };

      case 'in-progress':
        return {
          text: 'In Progress',
          color: 'from-[#2F6F45] to-[#70BD7D]',
          bgColor: 'bg-[#EAF5EC]',
          textColor: 'text-[#2F6F45]',
          icon: Clock
        };

      case 'completed':
        return {
          text: 'Completed',
          color: 'from-[#102018] to-[#2F6F45]',
          bgColor: 'bg-[#EAF5EC]',
          textColor: 'text-[#2F6F45]',
          icon: CheckCircle
        };

      case 'cancelled':
        return {
          text: 'Cancelled',
          color: 'from-[#102018] to-[#2F6F45]',
          bgColor: 'bg-[#EAF5EC]',
          textColor: 'text-[#102018]',
          icon: AlertCircle
        };

      default:
        return {
          text: 'Unknown',
          color: 'from-[#718078] to-[#102018]',
          bgColor: 'bg-[#F6F8F4]',
          textColor: 'text-[#102018]',
          icon: AlertCircle
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Transform API data to match component expectations
  const transformBookingData = (apiBooking) => {
    return {
      bookingId: apiBooking.bookingId || apiBooking.id,
      createdAt: apiBooking.createdAt || new Date().toISOString(),
      status: apiBooking.status || 'pending',
      scrapType: apiBooking.scrapType,
      quantity: apiBooking.quantity,
      pickupDate: apiBooking.pickupDate || apiBooking.pickupSlot,
      pickupTime: apiBooking.pickupTime || apiBooking.pickupSlot,
      address: apiBooking.address,
      city: apiBooking.city,
      pincode: apiBooking.pincode,
      landmark: apiBooking.landmark,
      photos: apiBooking.photoUrl
        ? [{ id: 1, preview: apiBooking.photoUrl }]
        : [],
      notes: apiBooking.notes || 'No additional notes provided.',
      name: apiBooking.name,
      mobile: apiBooking.mobile,
      estimatedValue: apiBooking.estimatedValue || '₹100-500',
      confirmationCall: apiBooking.confirmationCall || 'Pending',
      actualPayment: apiBooking.actualPayment || null,
      nextSteps: apiBooking.nextSteps || [
        'Our team will contact you soon',
        'Pickup will be scheduled',
        'Payment will be made after collection'
      ]
    };
  };

  const handleNewBooking = () => {
    navigate('/book-appointment');
  };

  const validateMobile = (value) => {
    const onlyDigits = value.replace(/\D/g, '');
    return onlyDigits.length === 10;
  };

  const handleSearch = (e) => {
    e.preventDefault();

    if (!validateMobile(mobileInput)) {
      showError('Please enter a valid 10-digit mobile number.');
      return;
    }

    console.log(
      'Searching for bookings with mobile:',
      mobileInput
    );

    setHasSearched(true);

    dispatch(fetchBookingsByMobile(mobileInput))
      .unwrap()
      .then((data) => {
        console.log('Backend data received:', data);
      })
      .catch((error) => {
        console.log('Backend error:', error);
        showError('Unable to fetch bookings. Please try again.');
      });
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F6F8F4] to-[#EAF5EC]">
        <div className="container-custom py-8">
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gradient-to-r from-[#102018] to-[#2F6F45] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Loader2 className="w-10 h-10 text-white animate-spin" />
            </div>

            <h1 className="text-3xl font-heading font-bold text-[#102018] mb-4">
              Loading Your Bookings
            </h1>

            <p className="text-[#718078]">
              Fetching your booking details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF]">

      {/* Header Section */}
      <section
        ref={headerRef}
        className="py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#EAF5EC] to-[#F6F8F4]"
      >
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-r from-[#102018] to-[#2F6F45] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Calendar className="w-10 h-10 md:w-12 md:h-12 text-[#70BD7D]" />
            </div>

            <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#102018] mb-4">
              My{' '}
              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Bookings
              </span>
            </h1>

            <p className="text-lg md:text-xl text-[#718078] max-w-2xl mx-auto leading-relaxed mb-8">
              Track your scrap pickup appointments and manage your bookings
            </p>

            <motion.button
              onClick={handleNewBooking}
              className="btn-primary text-base md:text-lg px-6 md:px-8 py-3 md:py-4 flex items-center space-x-2 mx-auto shadow-lg hover:shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus className="w-5 h-5" />
              <span>Book New Pickup</span>
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Search Section */}
      <section
        ref={searchRef}
        className="py-8 md:py-12 px-4 sm:px-6 lg:px-8"
      >
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-lg mx-auto"
          >
            <div className="bg-white border border-[#DDE8DF] rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">

              <div className="text-center mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-[#102018] mb-2">
                  Find Your Bookings
                </h2>

                <p className="text-[#718078] text-sm md:text-base">
                  Enter your mobile number to view your booking history
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="mobileSearch"
                    className="block text-sm font-medium text-[#102018] mb-2"
                  >
                    Mobile Number
                  </label>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      id="mobileSearch"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      placeholder="Enter 10-digit mobile number"
                      value={mobileInput}
                      onChange={(e) =>
                        setMobileInput(
                          e.target.value
                            .replace(/\D/g, '')
                            .slice(0, 10)
                        )
                      }
                      className="flex-1 px-4 py-3 border border-[#DDE8DF] rounded-xl focus:ring-2 focus:ring-[#2F6F45] focus:border-transparent transition-all duration-200 text-base text-[#102018]"
                    />

                    <motion.button
                      type="submit"
                      onClick={handleSearch}
                      className="btn-primary px-6 py-3 text-base font-medium"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Search className="w-4 h-4 mr-2 inline" />
                      Search
                    </motion.button>
                  </div>
                </div>

                <div className="bg-[#EAF5EC] border border-[#DDE8DF] rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-5 h-5 bg-[#70BD7D]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <HelpCircle
                        className="w-3.5 h-3.5 text-[#2F6F45]"
                      />
                    </div>

                    <p className="text-sm text-[#2F6F45]">
                      <strong>No login required!</strong> Simply enter your
                      mobile number to view your bookings.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </motion.div>
        </div>
      </section>

      {/* Bookings Section */}
      {(hasSearched || userData) && (
        <section
          ref={bookingsRef}
          className="py-8 md:py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#F6F8F4] to-[#EAF5EC]"
        >
          <div className="container-custom">

            {(() => {
              console.log('Display bookings:', bookings);
              console.log('Bookings from API:', bookings);
              console.log('Bookings length:', bookings.length);

              return bookings.length === 0 ? (

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="text-center py-12 md:py-16"
                >
                  <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg max-w-md mx-auto border border-[#DDE8DF]">

                    <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-r from-[#F6F8F4] to-[#EAF5EC] rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Calendar className="w-8 h-8 md:w-10 md:h-10 text-[#2F6F45]" />
                    </div>

                    <h3 className="text-xl md:text-2xl font-heading font-bold text-[#102018] mb-4">
                      No Bookings Found
                    </h3>

                    <p className="text-[#718078] mb-6 text-sm md:text-base">
                      No bookings found for mobile number{' '}
                      <span className="font-medium text-[#2F6F45]">
                        {mobileInput}
                      </span>
                    </p>

                    <motion.button
                      onClick={handleNewBooking}
                      className="btn-primary flex items-center space-x-2 mx-auto text-sm md:text-base px-6 py-3"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Plus className="w-4 h-4 md:w-5 md:h-5" />
                      <span>Book Your First Pickup</span>
                    </motion.button>
                  </div>
                </motion.div>

              ) : (

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="space-y-8"
                >

                  <div className="text-center mb-8 md:mb-12">
                    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg max-w-2xl mx-auto border border-[#DDE8DF]">

                      <h2 className="text-2xl md:text-3xl font-heading font-bold text-[#102018] mb-4">
                        Your <span className="text-gradient">Bookings</span>
                      </h2>

                      <p className="text-[#718078] text-sm md:text-base">
                        <span className="font-semibold text-[#2F6F45]">
                          {bookings.length}
                        </span>{' '}
                        booking{bookings.length !== 1 ? 's' : ''} found for
                        mobile number{' '}
                        <span className="font-medium text-[#2F6F45]">
                          {mobileInput}
                        </span>
                      </p>

                    </div>
                  </div>

                  <div className="space-y-8">

                    {bookings.map((booking, index) => {
                      const transformedBooking =
                        transformBookingData(booking);

                      const statusInfo =
                        getStatusInfo(transformedBooking.status);

                      const StatusIcon = statusInfo.icon;

                      return (
                        <motion.div
                          key={transformedBooking.bookingId}
                          className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-[#DDE8DF]"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.5,
                            delay: index * 0.1
                          }}
                          whileHover={{ y: -2 }}
                        >

                          {/* Booking Header with Status */}
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 md:mb-8 space-y-4 sm:space-y-0">

                            <div className="flex items-center space-x-3 md:space-x-4">

                              <div
                                className={`w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center ${statusInfo.bgColor}`}
                              >
                                <StatusIcon
                                  className={`w-6 h-6 md:w-8 md:h-8 ${statusInfo.textColor}`}
                                />
                              </div>

                              <div>
                                <h3 className="text-lg md:text-2xl font-heading font-bold text-[#102018] mb-1">
                                  Booking #{transformedBooking.bookingId}
                                </h3>

                                <p className="text-xs md:text-sm text-[#718078]">
                                  Created:{' '}
                                  {formatDate(
                                    transformedBooking.createdAt
                                  )}
                                </p>
                              </div>

                            </div>

                            <div
                              className={`px-3 py-2 md:px-4 md:py-2 rounded-full ${statusInfo.bgColor} ${statusInfo.textColor} text-xs md:text-sm font-medium flex items-center space-x-2 self-start sm:self-auto`}
                            >
                              <StatusIcon className="w-3 h-3 md:w-4 md:h-4" />
                              <span>{statusInfo.text}</span>
                            </div>

                          </div>

                          {/* Success Message */}
                          {(transformedBooking.status === 'confirmed' ||
                            transformedBooking.status === 'completed') && (
                            <div className="bg-[#EAF5EC] border border-[#DDE8DF] rounded-xl p-6 mb-6">

                              <div className="flex items-center space-x-3 mb-3">
                                <CheckCircle className="w-6 h-6 text-[#2F6F45]" />

                                <h4 className="text-lg font-heading font-bold text-[#102018]">
                                  {transformedBooking.status === 'completed'
                                    ? 'Booking Completed!'
                                    : 'Booking Confirmed!'}
                                </h4>
                              </div>

                              <p className="text-[#2F6F45]">
                                {transformedBooking.status === 'completed'
                                  ? 'Your scrap has been successfully collected and payment has been made.'
                                  : 'Your scrap pickup has been successfully scheduled. Our team will contact you soon to confirm the details.'}
                              </p>

                            </div>
                          )}

                          {/* Main Content Grid */}
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">

                            {/* Left Column */}
                            <div className="lg:col-span-2 space-y-4 md:space-y-6">

                              {/* Booking Details */}
                              <div>
                                <h4 className="text-lg font-heading font-bold text-[#102018] mb-4 flex items-center space-x-2">
                                  <User className="w-5 h-5 text-[#2F6F45]" />
                                  <span>Booking Details</span>
                                </h4>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">

                                  <div className="flex items-center space-x-3">
                                    <User className="w-5 h-5 text-[#2F6F45]" />
                                    <div>
                                      <p className="text-sm text-[#718078]">
                                        Name
                                      </p>
                                      <p className="font-medium text-[#102018]">
                                        {transformedBooking.name}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center space-x-3">
                                    <Phone className="w-5 h-5 text-[#2F6F45]" />
                                    <div>
                                      <p className="text-sm text-[#718078]">
                                        Mobile
                                      </p>
                                      <p className="font-medium text-[#102018]">
                                        {transformedBooking.mobile}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center space-x-3">
                                    <Package className="w-5 h-5 text-[#2F6F45]" />
                                    <div>
                                      <p className="text-sm text-[#718078]">
                                        Scrap Type
                                      </p>
                                      <p className="font-medium text-[#102018] capitalize">
                                        {transformedBooking.scrapType}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center space-x-3">
                                    <FileText className="w-5 h-5 text-[#2F6F45]" />
                                    <div>
                                      <p className="text-sm text-[#718078]">
                                        Quantity
                                      </p>
                                      <p className="font-medium text-[#102018] capitalize">
                                        {transformedBooking.quantity}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center space-x-3">
                                    <Calendar className="w-5 h-5 text-[#2F6F45]" />
                                    <div>
                                      <p className="text-sm text-[#718078]">
                                        Pickup Date
                                      </p>
                                      <p className="font-medium text-[#102018]">
                                        {transformedBooking.pickupDate}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center space-x-3">
                                    <Clock className="w-5 h-5 text-[#2F6F45]" />
                                    <div>
                                      <p className="text-sm text-[#718078]">
                                        Pickup Time
                                      </p>
                                      <p className="font-medium text-[#102018] capitalize">
                                        {transformedBooking.pickupTime}
                                      </p>
                                    </div>
                                  </div>

                                </div>
                              </div>

                              {/* Address */}
                              <div>
                                <h4 className="text-lg font-heading font-bold text-[#102018] mb-4 flex items-center space-x-2">
                                  <MapPin className="w-5 h-5 text-[#2F6F45]" />
                                  <span>Pickup Address</span>
                                </h4>

                                <div className="bg-[#F6F8F4] rounded-xl p-4">
                                  <p className="font-medium text-[#102018]">
                                    {transformedBooking.address},{' '}
                                    {transformedBooking.city} -{' '}
                                    {transformedBooking.pincode}
                                  </p>

                                  {transformedBooking.landmark && (
                                    <p className="text-sm text-[#718078] mt-1">
                                      Landmark:{' '}
                                      {transformedBooking.landmark}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Photos */}
                              {transformedBooking.photos.length > 0 && (
                                <div>
                                  <h4 className="text-lg font-heading font-bold text-[#102018] mb-4 flex items-center space-x-2">
                                    <Camera className="w-5 h-5 text-[#2F6F45]" />
                                    <span>Uploaded Photos</span>
                                  </h4>

                                  <div className="bg-[#F6F8F4] rounded-xl p-4">
                                    <p className="font-medium text-[#102018]">
                                      {transformedBooking.photos.length} photo
                                      {transformedBooking.photos.length !== 1
                                        ? 's'
                                        : ''}{' '}
                                      uploaded
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* Notes */}
                              <div>
                                <h4 className="text-lg font-heading font-bold text-[#102018] mb-4 flex items-center space-x-2">
                                  <FileText className="w-5 h-5 text-[#2F6F45]" />
                                  <span>Additional Notes</span>
                                </h4>

                                <div className="bg-[#F6F8F4] rounded-xl p-4">
                                  <p className="text-[#718078]">
                                    {transformedBooking.notes}
                                  </p>
                                </div>
                              </div>

                            </div>

                            {/* Right Column */}
                            <div className="space-y-6">

                              {/* Estimated Value */}
                              <div className="bg-gradient-to-r from-[#EAF5EC] to-[#F6F8F4] border border-[#DDE8DF] rounded-xl p-6">

                                <h4 className="text-lg font-heading font-bold text-[#102018] mb-3 flex items-center space-x-2">
                                  <DollarSign className="w-5 h-5 text-[#2F6F45]" />
                                  <span>Estimated Value</span>
                                </h4>

                                <p className="text-2xl font-bold text-[#2F6F45] mb-2">
                                  {transformedBooking.actualPayment ||
                                    transformedBooking.estimatedValue}
                                </p>

                                {transformedBooking.actualPayment && (
                                  <p className="text-sm text-[#2F6F45] font-medium">
                                    Final Payment Made
                                  </p>
                                )}

                              </div>

                              {/* Confirmation Call Status */}
                              <div className="bg-[#EAF5EC] border border-[#DDE8DF] rounded-xl p-6">

                                <h4 className="text-lg font-heading font-bold text-[#102018] mb-3 flex items-center space-x-2">
                                  <Phone className="w-5 h-5 text-[#2F6F45]" />
                                  <span>Confirmation Call</span>
                                </h4>

                                <p className="text-[#2F6F45] font-medium">
                                  {transformedBooking.confirmationCall}
                                </p>

                              </div>

                              {/* What Happens Next */}
                              <div className="bg-[#F6F8F4] border border-[#DDE8DF] rounded-xl p-6">

                                <h4 className="text-lg font-heading font-bold text-[#102018] mb-4 flex items-center space-x-2">
                                  <Clock className="w-5 h-5 text-[#2F6F45]" />
                                  <span>What Happens Next?</span>
                                </h4>

                                <div className="space-y-3">

                                  {transformedBooking.nextSteps.map(
                                    (step, stepIndex) => (
                                      <div
                                        key={stepIndex}
                                        className="flex items-start space-x-3"
                                      >
                                        <div className="w-6 h-6 bg-[#EAF5EC] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                          <span className="text-xs font-bold text-[#2F6F45]">
                                            {stepIndex + 1}
                                          </span>
                                        </div>

                                        <p className="text-sm text-[#718078]">
                                          {step}
                                        </p>
                                      </div>
                                    )
                                  )}

                                </div>
                              </div>

                            </div>
                          </div>

                        </motion.div>
                      );
                    })}

                  </div>
                </motion.div>
              );
            })}

          </div>
        </section>
      )}

      {/* Contact Info Section */}
      <section className="py-8 md:py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container-custom">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-lg mx-auto"
          >

            <div className="bg-gradient-to-r from-[#EAF5EC] to-[#F6F8F4] border border-[#DDE8DF] rounded-2xl p-6 md:p-8 shadow-lg">

              <div className="text-center mb-6">

                <div className="w-12 h-12 bg-gradient-to-r from-[#102018] to-[#2F6F45] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-[#70BD7D]" />
                </div>

                <h3 className="text-xl font-heading font-bold text-[#102018] mb-2">
                  Need Help?
                </h3>

                <p className="text-sm md:text-base text-[#718078]">
                  Contact us for any questions about your bookings
                </p>

              </div>

              <div className="space-y-4">

                <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
                  <div className="w-10 h-10 bg-[#EAF5EC] rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#2F6F45]" />
                  </div>

                  <div>
                    <p className="text-sm font-medium text-[#102018]">
                      Phone
                    </p>
                    <p className="text-sm font-semibold text-[#2F6F45]">
                      7278000038
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 p-3 bg-white/70 rounded-xl">
                  <div className="w-10 h-10 bg-[#EAF5EC] rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#2F6F45]" />
                  </div>

                  <div>
                    <p className="text-sm font-medium text-[#102018]">
                      Email
                    </p>
                    <p className="text-sm font-semibold text-[#2F6F45]">
                      scrapsectorbusiness@gmail.com
                    </p>
                  </div>
                </div>

              </div>

            </div>
          </motion.div>

        </div>
      </section>

    </div>
  );
};

export default MyBookings;